import React, { useState, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useFetchDetail, usePostDetail } from "@hooks/useFetchDetail";
import { useTranslation } from "react-i18next";
import { ServicesURL } from "@utils/utilsApi";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { PagesID } from "@utils/utilsConfigurations";
import ManageAccountingFormCst from "../ManageAccountingFormCst";

interface ManageAccountingInterface {
  onCancelAction: () => void;
  onSubmitAction: (data: any, list?: any) => void;
  row: any;
}

const ManageAccounting: React.FC<ManageAccountingInterface> = ({
  onCancelAction,
  onSubmitAction,
  row,
}) => {
  const { t } = useTranslation(["accounting-page"]);
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  const { columns } = useFetchDetail(
    ServicesURL.getProjectsCost,
    undefined,
    PagesID["accounting.form-code"],
  );

  const { trigger: triggerAccountingApproved } = usePostDetail(
    `${ServicesURL.postProjectsCost}/${row.id}/action/approve`,
  );

  const { trigger: triggerODAApproved } = usePostDetail(
    `${ServicesURL.postProjectsCost}/${row.id}/action/issue-po`,
  );

  const { trigger: triggerReceiptApproved } = usePostDetail(
    `${ServicesURL.postGoodsReceipts.replace(":costId", `${row.cost?.id || null}`)}/${row.id}/actions/approve`,
  );

  const onSubmit = async (inputForm: any) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });

    const objToSend: any =
      row.accounting_type === "ODA"
        ? { po_code: inputForm.type_code || null }
        : row.accounting_type === "RECEIPT"
          ? { code: inputForm.type_code || null }
          : { type_code: inputForm.type_code || null };

    try {
      if (row.accounting_type === "ODA") {
        await triggerODAApproved(objToSend);
      } else if (row.accounting_type === "RECEIPT") {
        await triggerReceiptApproved(objToSend);
      } else {
        await triggerAccountingApproved(objToSend);
      }
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      setOpenToast(undefined);
      console.error(error);
    }
  };

  const getColumns = useMemo(() => {
    return columns.map((el) => {
      return { ...el };
    });
  }, [columns]);

  const descriptionMapping = {
    RDA: t("form.rda-description"),
    BC_BE: t("form.bc-be-description"),
    ODA: t("form.oda-description"),
    RECEIPT: t("form.receipt-description"),
  };

  const description =
    descriptionMapping[
      row.accounting_type as keyof typeof descriptionMapping
    ] || "";

  return (
    <AnimationFadeIn>
      {columns && (
        <Box
          sx={{
            mt: "25px",
            maxHeight: "100vh",
            overflowY: "auto",
          }}
        >
          <Typography
            variant="h3"
            component="h2"
            sx={{
              mb: "8px",
              textAlign: "center",
            }}
          >
            {t("hero-title")}
          </Typography>

          <ManageAccountingFormCst
            columns={getColumns}
            onSubmit={onSubmit}
            backButton={() => {
              onCancelAction();
            }}
            description={description}
            fullWidth={true}
          />
        </Box>
      )}
      <ModalConfirmsCst
        open={!!openToast}
        title={t("hero-title-add")}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        callBackConfirmed={() => {
          setOpenToast(undefined);
          onCancelAction();
          onSubmitAction(undefined, undefined);
        }}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default ManageAccounting;
